/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getYear } from '../../../../utils';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { toFormatCurrency } from '../../../../../../utils/format';

const Q_REPORT = gql`
	query getEarlyWarningCompanyRiskRankingReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningCompanyRiskRankingReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

export default function RatioLifeCompanyRiskRankingSpreadSheetTable({
	year = null,
	companyType = null,
	companyId = null,
	groupingRiskBasedData = null,
	companyRiskRankingDataExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState([]);
	const [q1Cells, setQ1Cells] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(load, [year, companyType, companyId]);

	useEffect(() => data?.getEarlyWarningCompanyRiskRankingReport && setDetail(data?.getEarlyWarningCompanyRiskRankingReport), [data]);

	useEffect(() => {
		if (detail && groupingRiskBasedData) {
			quarterDetail(detail);
		}
	}, [detail, groupingRiskBasedData]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { year: getYear(year), companyType, companyId };
		loadReport({ variables });
	}

	function emptyCells(labels = [], i, groupingRiskBasedData = { q3Cells: [] }) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'CRR':
					return [
						Array.isArray(groupingRiskBasedData?.q3Cells[0]) ? groupingRiskBasedData.q3Cells[0] : [],
						Array.isArray(groupingRiskBasedData?.q3Cells[1]) ? groupingRiskBasedData.q3Cells[1] : [],
						Array.isArray(groupingRiskBasedData?.q3Cells[2]) ? groupingRiskBasedData.q3Cells[2] : [],
					];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'CRR':
					const [c1, c2, c3] = cells;
					const avg = (Number(c1[0]?.value || 0) + Number(c2[0]?.value || 0) + Number(c3[0]?.value || 0)) / 3;
					switch (i) {
						case 0:
							return toFormatCurrency(avg, null);
						case 1:
							return 1;
						case 2:
							return toFormatCurrency(avg, null) * 1;
						default:
							return 0;
					}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);
			const truncated = Math.trunc(score * 100) / 100;

			return [
				{
					value: toFormatCurrency(truncated, null),
					readOnly: true,
				},
			];
		});
	}

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		// Company Risk Ranking
		const companyRiskRanking =
			detailData?.q1 && detailData?.q1?.length
				? quarterCells({ labels: detailData?.labels, quarter: detailData?.q1 })
				: emptyCells(detailData?.labels, 0, groupingRiskBasedData);

		const numRows = detailData?.labels?.length;
		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		setQ1Cells(normalize(companyRiskRanking));

		companyRiskRankingDataExport({
			labels: detailData?.labels,
			q1Cells: companyRiskRanking,
		});
	}

	function quarterCells({ labels = [], quarter = null }) {
		return labels?.map((label) => {
			const labelObject = quarter?.find((e) => e?.code === label?.code);
			return labelObject?.values || [{ value: '0', readOnly: true }];
		});
	}

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<EWSLabels type="EWS" labels={detail?.labels} active={active} customBackgroundColor="#f7fafc" />
							<Flex>
								{q1Cells?.length > 0 && (
									<Spreadsheet
										data={q1Cells}
										onChange={(cells) => {
											setQ1Cells(
												q1Cells?.reduce((pre, cur, i) => {
													let localPre = pre;
													localPre.push([{ value: cur[0]?.value, readOnly: true }]);
													return localPre;
												}, []),
											);
										}}
										onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
										hideRowIndicators
										hideColumnIndicators
									/>
								)}
							</Flex>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
